import { create } from 'zustand';
import type { AgentUiActionId, CitationRegistry } from '@hector/shared-types';
import type { ChatMessage } from '@/types/chat';
import type { DisplayExchange } from '../types/displayExchange';
import type {
  ActiveTurn,
  ConversationTranscript,
  SessionDoneSnapshot,
} from '../types/transcript.types';

interface ConversationTranscriptStore {
  transcripts: Record<string, ConversationTranscript>;
  startHydration(conversationId: string): void;
  hydrateTranscript(
    conversationId: string,
    baseExchanges: DisplayExchange[],
    citationRegistry: CitationRegistry,
    fetchedAt?: number,
  ): void;
  failHydration(conversationId: string): void;
  beginTurn(
    conversationId: string,
    localTurnId: string,
    userMessage: ChatMessage,
    caseId?: string | null,
    uiActionCapabilities?: readonly AgentUiActionId[],
  ): boolean;
  bindOwnerSession(conversationId: string, sessionKey: string): void;
  /**
   * Réécrit le message de l'avocat du tour actif — la bulle créée avant le
   * téléversement annonce tous les fichiers, alors que certains peuvent ne pas
   * partir (#2066). Sans effet si le tour actif n'est pas `localTurnId` : un
   * tour annulé entre-temps ne doit jamais être ressuscité.
   */
  replaceActiveTurnUserMessage(conversationId: string, localTurnId: string, userMessage: ChatMessage): void;
  rollbackTurn(conversationId: string, localTurnId: string): void;
  commitTurn(snapshot: SessionDoneSnapshot): void;
  markStale(conversationId: string): void;
  removeTranscript(conversationId: string): void;
  clear(): void;
}

const initialState: Pick<ConversationTranscriptStore, 'transcripts'> = {
  transcripts: {},
};

function emptyTranscript(conversationId: string): ConversationTranscript {
  return {
    conversationId,
    ownerSessionKey: null,
    hydration: 'unhydrated',
    staleSince: null,
    baseExchanges: [],
    committedExchanges: [],
    activeTurn: null,
    citationRegistry: {},
  };
}

function ensureTranscript(
  transcripts: Record<string, ConversationTranscript>,
  conversationId: string,
): ConversationTranscript {
  return transcripts[conversationId] ?? emptyTranscript(conversationId);
}

function mergeHydratedBaseWithLocalCommits(
  baseExchanges: DisplayExchange[],
  committedExchanges: ConversationTranscript['committedExchanges'],
): Pick<ConversationTranscript, 'baseExchanges' | 'committedExchanges'> {
  // A just-created conversation can cache a successful REST read that only has
  // the persisted user row while the assistant is still streaming. Do not let
  // that stale echo delete the locally committed assistant answer after
  // navigation; only a base exchange carrying the same DB assistant id supersedes it.
  const hydratedAssistantIds = new Set(
    baseExchanges
      .map((exchange) => exchange.lastAssistantMessageId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0),
  );
  const retainedCommits = committedExchanges.filter(
    (exchange) => !!exchange.lastAssistantMessageId && !hydratedAssistantIds.has(exchange.lastAssistantMessageId),
  );
  const retainedBase = baseExchanges.filter(
    (exchange) => !isIncompleteEchoOfRetainedCommit(exchange, retainedCommits),
  );

  return {
    baseExchanges: retainedBase,
    committedExchanges: retainedCommits,
  };
}

function isIncompleteEchoOfRetainedCommit(
  exchange: DisplayExchange,
  committedExchanges: ConversationTranscript['committedExchanges'],
): boolean {
  if (exchange.timeline.length > 0 || exchange.lastAssistantMessageId) {
    return false;
  }

  const userContent = exchange.userMessage?.content.trim();
  if (!userContent) {
    return false;
  }

  return committedExchanges.some(
    (committed) => committed.userMessage?.content.trim() === userContent,
  );
}

export const useConversationTranscriptStore = create<ConversationTranscriptStore>()((set) => ({
  ...initialState,

  startHydration: (conversationId) => {
    set((state) => {
      const existing = ensureTranscript(state.transcripts, conversationId);
      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            hydration: 'hydrating',
          },
        },
      };
    });
  },

  hydrateTranscript: (conversationId, baseExchanges, citationRegistry, fetchedAt) => {
    set((state) => {
      const existing = ensureTranscript(state.transcripts, conversationId);

      // A ready transcript is already the local source of truth. Only stale/error/
      // in-flight hydration may replace the REST base, and replacement happens in
      // this single mutation so the UI never sees a half-cleared transcript.
      if (existing.hydration === 'ready') {
        return state;
      }

      if (existing.activeTurn !== null) {
        // A live local turn is the UI authority for the current prompt. The
        // first REST read after a just-created conversation can race behind
        // persistence and return the same user message while the assistant is
        // still streaming; hydrating that echo into the base would render the
        // prompt twice (history + active turn).
        return state;
      }

      const mergedTranscript = mergeHydratedBaseWithLocalCommits(
        baseExchanges,
        existing.committedExchanges,
      );

      // WHY (#1888) : quand le transcript est marqué périmé, la page tient encore
      // l'ancien historique en cache et le relit AVANT le retour du refetch (l'effet
      // d'hydratation se rejoue dès que le tour local disparaît). Cette lecture est
      // bonne à afficher — elle rend à l'écran la question que portait le tour local —
      // mais elle ne doit pas lever le marquage : un transcript `ready` refuse toute
      // réhydratation, et la réponse finale, arrivée avec le refetch, n'apparaissait
      // jamais. Seule une lecture POSTÉRIEURE au marquage le lève.
      const predatesStaleness = existing.hydration === 'stale'
        && existing.staleSince !== null
        && fetchedAt !== undefined
        && fetchedAt <= existing.staleSince;

      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            hydration: predatesStaleness ? 'stale' : 'ready',
            staleSince: predatesStaleness ? existing.staleSince : null,
            baseExchanges: mergedTranscript.baseExchanges,
            committedExchanges: mergedTranscript.committedExchanges,
            citationRegistry,
          },
        },
      };
    });
  },

  failHydration: (conversationId) => {
    set((state) => {
      const existing = ensureTranscript(state.transcripts, conversationId);
      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            hydration: 'error',
          },
        },
      };
    });
  },

  beginTurn: (conversationId, localTurnId, userMessage, caseId, uiActionCapabilities) => {
    let didBegin = false;
    set((state) => {
      const existing = ensureTranscript(state.transcripts, conversationId);
      const activeTurn: ActiveTurn = {
        localTurnId,
        userMessage,
        ...(caseId !== undefined ? { caseId } : {}),
        ...(uiActionCapabilities !== undefined
          ? { uiActionCapabilities: [...uiActionCapabilities] }
          : {}),
      };

      if (
        existing.activeTurn !== null
        && existing.activeTurn.localTurnId !== localTurnId
      ) {
        didBegin = false;
        return state;
      }

      didBegin = true;
      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            activeTurn,
          },
        },
      };
    });
    return didBegin;
  },

  bindOwnerSession: (conversationId, sessionKey) => {
    set((state) => {
      const existing = ensureTranscript(state.transcripts, conversationId);
      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            ownerSessionKey: sessionKey,
          },
        },
      };
    });
  },

  replaceActiveTurnUserMessage: (conversationId, localTurnId, userMessage) => {
    set((state) => {
      const existing = state.transcripts[conversationId];
      if (
        existing === undefined
        || existing.activeTurn === null
        || existing.activeTurn.localTurnId !== localTurnId
      ) {
        return state;
      }

      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            activeTurn: { ...existing.activeTurn, userMessage },
          },
        },
      };
    });
  },

  rollbackTurn: (conversationId, localTurnId) => {
    set((state) => {
      const existing = state.transcripts[conversationId];
      if (
        existing === undefined
        || existing.activeTurn === null
        || existing.activeTurn.localTurnId !== localTurnId
      ) {
        return state;
      }

      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            activeTurn: null,
          },
        },
      };
    });
  },

  commitTurn: (snapshot) => {
    set((state) => {
      const existing = state.transcripts[snapshot.conversationId];
      if (existing === undefined) {
        return state;
      }

      const alreadyCommitted = existing.committedExchanges.some(
        (exchange) => exchange.localTurnId === snapshot.localTurnId,
      );
      if (alreadyCommitted) {
        return state;
      }

      if (
        existing.ownerSessionKey !== snapshot.sessionKey
        || existing.activeTurn === null
        || existing.activeTurn.localTurnId !== snapshot.localTurnId
      ) {
        return state;
      }

      const committedExchange = {
        id: `turn:${snapshot.localTurnId}`,
        userMessage: existing.activeTurn.userMessage,
        timeline: [...snapshot.timeline],
        phase: 'history' as const,
        ...(existing.activeTurn.caseId !== undefined ? { caseId: existing.activeTurn.caseId } : {}),
        ...(existing.activeTurn.uiActionCapabilities !== undefined
          ? { uiActionCapabilities: [...existing.activeTurn.uiActionCapabilities] }
          : {}),
        lastAssistantMessageId: snapshot.lastAssistantMessageId,
        localTurnId: snapshot.localTurnId,
        conversationId: snapshot.conversationId,
        reason: snapshot.reason,
        completedAt: Date.now(),
      };

      return {
        transcripts: {
          ...state.transcripts,
          [snapshot.conversationId]: {
            ...existing,
            committedExchanges: [...existing.committedExchanges, committedExchange],
            activeTurn: null,
            citationRegistry: {
              ...existing.citationRegistry,
              ...(snapshot.citationRegistry ?? {}),
            },
          },
        },
      };
    });
  },

  markStale: (conversationId) => {
    set((state) => {
      const existing = state.transcripts[conversationId];
      if (existing === undefined) {
        return state;
      }

      return {
        transcripts: {
          ...state.transcripts,
          [conversationId]: {
            ...existing,
            hydration: 'stale',
            staleSince: Date.now(),
          },
        },
      };
    });
  },

  removeTranscript: (conversationId) => {
    set((state) => {
      if (state.transcripts[conversationId] === undefined) {
        return state;
      }

      const transcripts = { ...state.transcripts };
      delete transcripts[conversationId];
      return { transcripts };
    });
  },

  clear: () => {
    set(initialState);
  },
}));
